import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowRight, Globe } from "lucide-react";
import { NAV, PILLARS, LOGO_WHITE } from "@/data/content";
import { useLang } from "@/context/LanguageContext";

const Logo = ({ landing }) => (
  <Link to="/" className="flex shrink-0 items-center" data-testid="header-logo-link">
    <img src={LOGO_WHITE} alt="ERAM DIGITAL" width="421" height="183" className={`w-auto object-contain ${landing ? "h-[60px] sm:h-[72px]" : "h-10 sm:h-12"}`} data-testid="header-logo" />
  </Link>
);

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`brand-header fixed inset-x-0 top-0 z-50 border-b border-white/10 transition-shadow duration-300 ${scrolled ? "shadow-lg" : ""}`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 xl:px-8">
        <Logo landing={location.pathname === "/"} />

        <nav className="hidden items-center gap-1 xl:flex" aria-label="Primary">
          {NAV.map((item) =>
            item.key === "services" ? (
              <div
                key={item.key}
                className="relative"
                onMouseEnter={() => setMegaOpen(true)}
                onMouseLeave={() => setMegaOpen(false)}
              >
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      isActive ? "text-[#0A0A0A]" : "text-[#4B5563] hover:text-[#0A0A0A]"
                    }`
                  }
                  data-testid="nav-services"
                >
                  {t(`nav.${item.key}`)}
                  <ChevronDown className={`h-4 w-4 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
                </NavLink>
                <AnimatePresence>
                  {megaOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute left-1/2 top-full w-[640px] -translate-x-1/2 pt-3"
                    >
                      <div className="glass grid grid-cols-2 gap-2 rounded-2xl p-4 shadow-2xl">
                        {PILLARS.map((p) => (
                          <Link
                            key={p.id}
                            to={`/services#${p.id}`}
                            className="group rounded-xl p-4 transition-colors hover:bg-[#F3F4F6]"
                            data-testid={`mega-${p.id}`}
                          >
                            <div className="mb-1 flex items-center justify-between">
                              <span className="font-display text-sm font-semibold text-[#0A0A0A]">{p.name}</span>
                              <ArrowRight className="h-4 w-4 text-[#6B7280] transition-transform group-hover:translate-x-1 group-hover:text-brand-violet" />
                            </div>
                            <p className="text-xs leading-relaxed text-[#4B5563]">{p.tagline}</p>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink
                key={item.key}
                to={item.path}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    isActive ? "text-[#0A0A0A]" : "text-[#4B5563] hover:text-[#0A0A0A]"
                  }`
                }
                data-testid={`nav-${item.key}`}
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            className="hidden items-center gap-1.5 rounded-full border border-[#E5E7EB] px-3 py-2 text-xs font-medium text-[#4B5563] transition-colors hover:border-[#D1D5DB] hover:text-[#0A0A0A] sm:flex"
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            <Globe className="h-3.5 w-3.5" />
            {lang === "en" ? "العربية" : "EN"}
          </button>
          <Link
            to="/contact"
            className="hidden rounded-full gradient-bg px-5 py-2.5 text-sm font-semibold text-white shadow-[0_0_20px_rgba(207,55,79,0.4)] transition-transform hover:scale-[1.03] xl:inline-block"
            data-testid="header-consult-cta"
          >
            {t("cta.consult")}
          </Link>
          <button
            className="rounded-lg p-2 text-[#374151] xl:hidden"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-[#E5E7EB] bg-white/95 backdrop-blur-xl xl:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              {NAV.map((item) => (
                <NavLink
                  key={item.key}
                  to={item.path}
                  className={({ isActive }) =>
                    `rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                      isActive ? "bg-[#F3F4F6] text-[#0A0A0A]" : "text-[#4B5563]"
                    }`
                  }
                  data-testid={`mobile-nav-${item.key}`}
                >
                  {t(`nav.${item.key}`)}
                </NavLink>
              ))}
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={toggle}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-[#E5E7EB] px-4 py-3 text-sm font-medium text-[#374151]"
                  data-testid="mobile-lang-toggle"
                >
                  <Globe className="h-4 w-4" />
                  {lang === "en" ? "العربية" : "English"}
                </button>
              </div>
              <Link
                to="/contact"
                className="mt-1 rounded-lg gradient-bg px-4 py-3 text-center text-sm font-semibold text-white"
                data-testid="mobile-consult-cta"
              >
                {t("cta.consult")}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
